import React, { useEffect, useState } from "react";
import usePlacementStore from "../../store/placementStore";
import ProjectManager from "../../canvas/ProjectManager";
import ParametricDotsLibrary from "../../libraries/ParametricDotsLibrary";
import layoutEngine from "../../libraries/layoutEngine";
import selectionLibrary from "../../libraries/SelectionLibrary";
import {
  isLocked,
  isVisible,
  setLock,
  setVisibility,
  toggleVisibility,
} from "../../canvas/VisibilityManager";
import {
  getPlacedPCell,
  removePlacedPCell,
} from "../store/placedPCellStore";

const BlocksTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [, setVisibilityVersion] = useState(0);
  const [lockedNames, setLockedNames] = useState(() => {
  return new Set(Array.from(ParametricDotsLibrary.frozenRefNames || []));
});


useEffect(() => {
  const syncLockedNames = (e) => {
    const names =
      e?.detail?.frozenRefNames ||
      Array.from(ParametricDotsLibrary.frozenRefNames || []);

    setLockedNames(new Set(names));
  };

  // ✅ tab wapas open hone par latest freeze state restore
  syncLockedNames();

  window.addEventListener("parametric-freeze-changed", syncLockedNames);

  return () => {
    window.removeEventListener("parametric-freeze-changed", syncLockedNames);
  };
}, []);

  const {
    previewPatterns,
    selectedPatterns,
    togglePatternSelection,
    setSelectedPatterns,
    removePattern
  } = usePlacementStore();

  const filteredItems = previewPatterns
  .filter((item) => {
    const placed = getPlacedPCell(item.name);

    // ✅ Draft/next preview PCell ko Blocks list se hide karo
    if (placed?.isDraftPreview === true) return false;

    return item.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const allVisibleSelected =
    filteredItems.length > 0 &&
    filteredItems.every((item) => selectedPatterns.includes(item.name));

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const allNames = filteredItems.map((item) => item.name);
      setSelectedPatterns(allNames);
    } else {
      setSelectedPatterns([]);
    }
  };

  const handleDelete = (patternName, e) => {
    e.stopPropagation();

    if (isLocked(patternName)) {
      window.alert(`Unlock "${patternName}" before deleting it.`);
      return;
    }

    if (!window.confirm(`Delete "${patternName}"?`)) return;

    const engine = window.canvasEngine;
    const affectedParents = new Map();

    engine?.recordState?.();

    for (const cell of [
      ...(layoutEngine.getCells?.() || []),
      ...(layoutEngine.getUploadedCells?.() || []),
    ]) {
      const removedIds = (cell.instances || [])
        .filter((instance) => instance.ref === patternName)
        .map((instance) => instance.id)
        .filter(Boolean);

      if (!removedIds.length) continue;

      cell.instances = (cell.instances || []).filter(
        (instance) => instance.ref !== patternName
      );

      const existingIds = affectedParents.get(cell.name) || [];
      affectedParents.set(cell.name, [...existingIds, ...removedIds]);
    }

    for (const [parentCellName, instanceIds] of affectedParents) {
      ParametricDotsLibrary.removeConnectionsForInstanceIds(
        engine,
        parentCellName,
        instanceIds
      );
    }

    removePlacedPCell(patternName);
    layoutEngine.deleteCell?.(patternName);
    ParametricDotsLibrary.setFrozenRefName(patternName, false);
    setLock(patternName, false);

    if (!isVisible(patternName)) {
      setVisibility(patternName, true);
    }

    if (engine?.selectedObjects?.length) {
      engine.selectedObjects = engine.selectedObjects.filter(
        (object) => object?.ref?.ref !== patternName
      );
      selectionLibrary.setSelection(engine.selectedObjects);
    }

    if (ProjectManager.getSelectedInstanceCell?.() === patternName) {
      ProjectManager.setSelectedInstanceCell?.("");
      window.dispatchEvent(
        new CustomEvent("pcell-instance-selected", {
          detail: { cellName: "", instanceId: "", parentCellName: "" },
        })
      );
    }

    const wasSelectedOnly =
      usePlacementStore.getState().selectedOnlyPCellName === patternName;

    removePattern(patternName);

    if (wasSelectedOnly) {
      ProjectManager.setShowOnlySelected(false);
    }

    window.dispatchEvent(new Event("hierarchy-updated"));
    engine?.draw?.();
  };

  const handleVisibilityToggle = (patternName, e) => {
    e.stopPropagation();
    toggleVisibility(patternName);
    setVisibilityVersion((version) => version + 1);
  };


const handleLockToggle = (patternName, e) => {
  e.stopPropagation();

  setLockedNames((prev) => {
    const next = new Set(prev);
    const shouldLock = !next.has(patternName);

    if (shouldLock) {
      next.add(patternName);
    } else {
      next.delete(patternName);
    }

    // ✅ Lock = Freeze, Unlock = Unfreeze
    ParametricDotsLibrary.setFrozenRefName(patternName, shouldLock);
    setLock(patternName, shouldLock);

    return next;
  });

  window.canvasEngine?.draw?.();
};



  // inside BlocksTab.jsx

// BlocksTab.jsx

const handleDotGridApply = (gridSize) => {
  const selectedNames = selectedPatterns;
  const parentCellName = ProjectManager.getSelectedCell();

  if (!parentCellName) {
    alert("⚠️ No selected (parent) cell found.");
    return;
  }

  const parentCell =
    window.layoutEngine?.getMergedCell?.(parentCellName) ||
    window.layoutEngine?.getCellByName?.(parentCellName) ||
    window.layoutEngine?.getUploadedCell?.(parentCellName);

  if (!parentCell) {
    alert("⚠️ Parent cell object not found.");
    return;
  }

  const createdCell = window.layoutEngine?.getCellByName?.(parentCellName);
  const uploadedCell = window.layoutEngine?.getUploadedCell?.(parentCellName);

  

  const selectedObjects = [];

  for (const refName of selectedNames) {
    const refBBox = window.layoutEngine?.getBoundingBox?.(refName);
    if (!refBBox) continue;

    const insts = (parentCell.instances || []).filter((i) => i.ref === refName);

    for (const inst of insts) {
      if (!inst.id) inst.id = `inst_${Date.now()}_${Math.random()}`;

      const isUploaded =
        !!uploadedCell?.instances?.some((i) => i.id === inst.id) &&
        !createdCell?.instances?.some((i) => i.id === inst.id);

      selectedObjects.push({
        id: inst.id,
        type: "instance",
        ref: inst,
        isUploaded,

        name: `${refName}__${inst.id}`,
        refName,
        instanceId: inst.id,
        parentCellName,

        x: (inst.x || 0) + (refBBox.x || 0),
        y: (inst.y || 0) + (refBBox.y || 0),
        width: refBBox.width,
        height: refBBox.height,
      });
    }
  }

  if (!selectedObjects.length) {
    alert("⚠️ No instance bounding boxes found in selected cell.");
    return;
  }

  window.canvasEngine.selectedObjects = selectedObjects;

  ParametricDotsLibrary.setTargets(selectedObjects, gridSize);
  ParametricDotsLibrary.redraw(window.canvasEngine, { refresh: false });
  ParametricDotsLibrary.applyToEngine(window.canvasEngine);
  window.canvasEngine.draw();
};








  return (
    <div style={{ padding: "10px", color: "#eee", fontSize: "13px" }}>
      {/* ✅ Select All */}
      {/* ✅ Select All + Grid Icons */}
<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
  {/* Select All Checkbox */}
  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <input
      type="checkbox"
      checked={allVisibleSelected}
      onChange={handleSelectAll}
    />
    Select All
  </label>

  {/* Grid Icons */}
  <div   onClick={() => handleDotGridApply(3)} style={{ display: "flex", gap: "8px" }}>
    {/* 3x3 */}
    <div style=   {getGridIconStyle(3)} title="3x3 Grid">
      {Array(9).fill().map((_, i) => (
        <div key={i} style={dotStyle}></div>
      ))}
    </div>

    {/* 4x4 */}
    <div      onClick={() => handleDotGridApply(4)} style={getGridIconStyle(4)} title="4x4 Grid">
      {Array(16).fill().map((_, i) => (
        <div key={i} style={dotStyle}></div>
      ))}
    </div>

    {/* 5x5 */}
    <div   onClick={() => handleDotGridApply(5)} style={getGridIconStyle(5)} title="5x5 Grid">
      {Array(25).fill().map((_, i) => (
        <div key={i} style={dotStyle}></div>
      ))}
    </div>
  </div>
</div>


      {/* 🔍 Search */}
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          width: "100%",
          padding: "6px",
          backgroundColor: "#2a2a2a",
          color: "#fff",
          border: "1px solid #444",
          borderRadius: "3px",
          fontSize: "13px",
        }}
      />

      <button
  onClick={() => ParametricDotsLibrary.connectSelectedDots(window.canvasEngine)}
  style={{
    marginLeft: "10px",
    padding: "6px 10px",
    backgroundColor: "#2a2a2a",
    border: "1px solid #444",
    borderRadius: "4px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
  }}
  title="Connect selected dots"
>
  CONNECT
</button>

      {/* List */}
      <div style={{ marginTop: "10px" }}>
        {filteredItems.map((item, index) => {
          const isSelected = selectedPatterns.includes(item.name);
          const itemIsVisible = isVisible(item.name);
          return (
            <div
              key={index}
              onClick={() => togglePatternSelection(item.name)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: isSelected ? "#2c3e50" : "#1e1e1e",
                padding: "8px 10px",
                borderBottom: "1px solid #333",
                borderRadius: "4px",
                marginBottom: "6px",
                cursor: "pointer",
              }}
            >
              <div>{item.name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px" }}>
  <span
    title={itemIsVisible ? "Hide PCell" : "Show PCell"}
    onClick={(e) => handleVisibilityToggle(item.name, e)}
    style={{ cursor: "pointer", opacity: itemIsVisible ? 1 : 0.4 }}
  >
    👁
  </span>

  <span
    title={lockedNames.has(item.name) ? "Unlock / Unfreeze PCell" : "Lock / Freeze PCell"}
    onClick={(e) => handleLockToggle(item.name, e)}
    style={{
      cursor: "pointer",
      color: lockedNames.has(item.name) ? "#00e676" : "#ddd",
      filter: lockedNames.has(item.name)
        ? "drop-shadow(0 0 4px rgba(0,230,118,0.7))"
        : "none",
      transform: lockedNames.has(item.name) ? "scale(1.08)" : "scale(1)",
      transition: "all 0.15s ease",
    }}
  >
    {lockedNames.has(item.name) ? "🔒" : "🔓"}
  </span>

  <span
    title="Delete"
    onClick={(e) => handleDelete(item.name, e)}
    style={{ cursor: "pointer" }}
  >
    🗑
  </span>
</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};



const getGridIconStyle = (size) => ({
  width: "30px",
  height: "30px",
  display: "grid",
  gridTemplateColumns: `repeat(${size}, 1fr)`,
  gridTemplateRows: `repeat(${size}, 1fr)`,
  gap: "2px",
  backgroundColor: "#2a2a2a",
  border: "1px solid #444",
  borderRadius: "4px",
  padding: "3px",
  cursor: "pointer",
});

const dotStyle = {
  width: "4px",
  height: "4px",
  backgroundColor: "#ccc",
  borderRadius: "50%",
};


export default BlocksTab;
